/**
 * Final-users-only company setting (rendered inside the Profile tab) that
 * switches a company to deferred platform-fee billing.
 *
 * Flow (on): flipping the toggle on always opens the payment-method modal —
 * deferred billing is only enabled once a card on file has been *selected*
 * (mirroring the "Custom Payment" install flow), never just because a card happens
 * to exist. The modal lets the user pick an existing card or add a new one, sets
 * the chosen card as default, and only then switches the company to `deferred`.
 * Cancelling/closing the modal (or any failure) reverts the toggle to off.
 *
 * Flow (off): flipping the toggle off opens a confirmation modal — switching back
 * to standard tells the backend to invoice any fees accrued in the open period and
 * stop deferring future transactions. Cancelling (or any failure) reverts the
 * toggle to on.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { skipToken } from '@reduxjs/toolkit/query';

import { Column, Row } from '@/components/SettingsLayout';
import { Company } from '@/context/types';
import { FINAL_USERS } from '@/enums';
import { useGetUser } from '@/hooks';
import { useUpdateCompanyDeferFeesMutation } from '@/store/slices/companySlice';
import { useGetStripeConfigQuery } from '@/store/slices/stripeApiSlice';
import { Divider, SwitchInput, Text } from '@/ui';

import DeferredFeesDisableModal from './DeferredFeesDisableModal';
import DeferredFeesPaymentModal from './DeferredFeesPaymentModal';

type Props = {
  company: Company;
};

function DeferredFeesSetting({ company }: Props) {
  const { t } = useTranslation();
  const { currentUser } = useGetUser();
  const isFinalUser = FINAL_USERS.includes(currentUser.type);
  const [updateCompanyDeferFees, { isLoading }] = useUpdateCompanyDeferFeesMutation();

  // The Stripe customer id lives in the company's EAV config, not the (legacy,
  // often-empty) company.stripe field, so fetch it by companyId.
  const { data: stripeConfig, isFetching: isConfigLoading } = useGetStripeConfigQuery(
    isFinalUser ? company._id : skipToken,
  );
  const stripeCustomerId = stripeConfig?.stripeCustomerId;

  const isDeferred = company.deferFees === true;
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [disableModalOpen, setDisableModalOpen] = useState(false);

  // The switch is driven by the persisted payment type (the single source of truth).
  // While a confirmation modal is open it shows the pending intent optimistically so
  // it doesn't snap back during the post-mutation refetch. The mutation invalidates the
  // `company` tag, so `isDeferred` updates on its own once the change is persisted.
  const checked = paymentModalOpen || (isDeferred && !disableModalOpen);

  // Close the open confirmation modal once the persisted state reflects the action.
  useEffect(() => {
    if (isDeferred) setPaymentModalOpen(false);
    else setDisableModalOpen(false);
  }, [isDeferred]);

  if (!isFinalUser) {
    return null;
  }

  const handleToggle = (value: boolean) => {
    if (value) {
      // Turning on -> go through payment-method selection. Deferred billing is only
      // enabled once a card has been selected (never just because one exists).
      if (isDeferred) {
        return;
      }
      if (!stripeCustomerId) {
        toast.error(t('Companies.deferred_fees_no_stripe_customer'));
        return;
      }
      setPaymentModalOpen(true);
    } else {
      // Turning off -> confirm before switching back to standard billing.
      if (!isDeferred) {
        return;
      }
      setDisableModalOpen(true);
    }
  };

  const handleConfirm = async () => {
    try {
      await updateCompanyDeferFees({
        companyId: company._id,
        deferFees: true,
      }).unwrap();
      toast.success(t('Companies.deferred_fees_enabled_success'));
      // The modal closes via the effect once `isDeferred` flips after the refetch.
    } catch {
      toast.error(t('Companies.deferred_fees_enable_error'));
      setPaymentModalOpen(false);
    }
  };

  const handleDisableConfirm = async () => {
    try {
      await updateCompanyDeferFees({
        companyId: company._id,
        deferFees: false,
      }).unwrap();
      toast.success(t('Companies.deferred_fees_disabled_success'));
    } catch {
      toast.error(t('Companies.deferred_fees_disable_error'));
      setDisableModalOpen(false);
    }
  };

  return (
    <>
      <Divider />
      <Row fullWidth justify='justify-between' align='center' gapSize='24'>
        <Column gapSize='4'>
          <Text size='s' weight='semi-bold'>
            {t('Companies.deferred_fees')}
          </Text>
          <Text size='xs' weight='light-medium'>
            {t('Companies.deferred_fees_comment')}
          </Text>
        </Column>
        <SwitchInput
          checked={checked}
          onInput={handleToggle}
          disabled={isLoading || paymentModalOpen || disableModalOpen || isConfigLoading}
          testId='deferred-fees-toggle'
        />
      </Row>

      {stripeCustomerId && (
        <DeferredFeesPaymentModal
          open={paymentModalOpen}
          companyId={company._id}
          stripeCustomerId={stripeCustomerId}
          isConfirming={isLoading}
          onConfirm={handleConfirm}
          onCancel={() => setPaymentModalOpen(false)}
        />
      )}

      <DeferredFeesDisableModal
        open={disableModalOpen}
        isConfirming={isLoading}
        onConfirm={handleDisableConfirm}
        onCancel={() => setDisableModalOpen(false)}
      />
    </>
  );
}

export default DeferredFeesSetting;
